import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';

import { endpoints } from 'global/endpoints';
import { useFeatureStore } from 'store/useFeatureStore';
import { getSubscriptionStatus, UserDetails, useUserStore } from 'store/useUserStore';
import http from 'utils/http';

type UserDetailsResponse = {
  payload: UserDetails;
};

const fetchUserDetails = (): Promise<UserDetailsResponse> => {
  const newEndpoints = endpoints.profile.userDetails;
  return http().get(newEndpoints);
};

export function useFetchUserDetails(options?: any): UseQueryResult<UserDetailsResponse, unknown> {
  const addUserDetails = useUserStore(state => state.addUserDetails);
  const setSubscriptionStatus = useUserStore(state => state.setSubscriptionStatus);
  const { addFeatures } = useFeatureStore(state => state);

  const queryResult = useQuery<UserDetailsResponse, unknown>({
    queryKey: ['fetchUserDetails'],
    queryFn: () => fetchUserDetails(),
    ...options,
  });

  // Handle side effects when data is successfully fetched
  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      const payload = queryResult.data?.payload;
      if (!payload) return;
      addUserDetails(payload);

      if (payload?.features) {
        addFeatures(payload.features);
      }

      const subscriptionStatus = getSubscriptionStatus(payload?.companyInfo?.valid_until);
      setSubscriptionStatus(subscriptionStatus);
    }

    if (queryResult.isError) {
      setSubscriptionStatus('over');
    }
  }, [
    queryResult.isSuccess,
    queryResult.isError,
    queryResult.data,
    addUserDetails,
    addFeatures,
    setSubscriptionStatus,
  ]);

  return queryResult;
}
